<template>
  <v-container fluid class="pa-0">
    <!-- Overall Statistics -->
    <v-card outlined class="stats-card mb-3">
      <v-card-text class="pa-4">
        <div class="stats-grid">
          <div class="stat-item">
            <div class="compact-stat">
              <div class="stat-number primary--text">
                {{ totalSettledCases }}
              </div>
              <div class="stat-label">Total Settled Cases</div>
            </div>
          </div>
          <div class="stat-item">
            <div class="compact-stat">
              <div class="stat-number warning--text">
                {{ policyLimitsExhaustedPercentage }}
              </div>
              <div class="stat-label">Policy Limits Exhausted</div>
              <div class="stat-subtitle">
                {{ policyLimitsExhaustedCount }} / {{ totalSettledCases }} cases
              </div>
            </div>
          </div>
          <div class="stat-item">
            <div class="compact-stat">
              <div class="stat-number success--text">
                {{ averageFeeFormatted }}
              </div>
              <div class="stat-label">
                Average Fee per Case (>$2K)
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon small class="ml-1" v-bind="attrs" v-on="on"
                      >mdi-information-outline</v-icon
                    >
                  </template>
                  <span>Excludes cases with fees ≤ $2,000</span>
                </v-tooltip>
              </div>
              <div class="stat-subtitle">
                {{ qualifyingCasesCount }} cases included
              </div>
            </div>
          </div>
          <div class="stat-item">
            <div class="compact-stat">
              <div class="stat-number orange--text">
                {{ normalRangeAverageFeeFormatted }}
              </div>
              <div class="stat-label">
                Normal Range Average
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon small class="ml-1" v-bind="attrs" v-on="on"
                      >mdi-information-outline</v-icon
                    >
                  </template>
                  <span>Excludes cases with fees ≤ $2,000 or > $1,000,000</span>
                </v-tooltip>
              </div>
              <div class="stat-subtitle">
                {{ normalRangeCasesCount }} cases included
              </div>
            </div>
          </div>
          <div class="stat-item">
            <div class="compact-stat">
              <div class="stat-number info--text">
                {{ preLitigationAverageFeeFormatted }}
              </div>
              <div class="stat-label">
                Pre-Litigation Average (>$2K)
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon small class="ml-1" v-bind="attrs" v-on="on"
                      >mdi-information-outline</v-icon
                    >
                  </template>
                  <span>Pre-litigation cases with fees > $2,000</span>
                </v-tooltip>
              </div>
              <div class="stat-subtitle">
                {{ preLitigationQualifyingCases.length }} qualifying /
                {{ preLitigationCases.length }} total
              </div>
            </div>
          </div>
          <div class="stat-item">
            <div class="compact-stat">
              <div class="stat-number teal--text">
                {{ preLitigationNormalRangeAverageFeeFormatted }}
              </div>
              <div class="stat-label">
                Pre-Lit Normal Range
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon small class="ml-1" v-bind="attrs" v-on="on"
                      >mdi-information-outline</v-icon
                    >
                  </template>
                  <span
                    >Pre-litigation cases with fees > $2,000 and ≤
                    $1,000,000</span
                  >
                </v-tooltip>
              </div>
              <div class="stat-subtitle">
                {{ preLitigationNormalRangeCases.length }} normal /
                {{ preLitigationCases.length }} total
              </div>
            </div>
          </div>
          <div class="stat-item">
            <div class="compact-stat">
              <div class="stat-number error--text">
                {{ litigationAverageFeeFormatted }}
              </div>
              <div class="stat-label">
                Litigation Average (>$2K)
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon small class="ml-1" v-bind="attrs" v-on="on"
                      >mdi-information-outline</v-icon
                    >
                  </template>
                  <span>Litigation cases with fees > $2,000</span>
                </v-tooltip>
              </div>
              <div class="stat-subtitle">
                {{ litigationQualifyingCases.length }} qualifying /
                {{ litigationCases.length }} total
              </div>
            </div>
          </div>
          <div class="stat-item">
            <div class="compact-stat">
              <div class="stat-number deep-purple--text">
                {{ litigationNormalRangeAverageFeeFormatted }}
              </div>
              <div class="stat-label">
                Litigation Normal Range
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon small class="ml-1" v-bind="attrs" v-on="on"
                      >mdi-information-outline</v-icon
                    >
                  </template>
                  <span
                    >Litigation cases with fees > $2,000 and ≤ $1,000,000</span
                  >
                </v-tooltip>
              </div>
              <div class="stat-subtitle">
                {{ litigationNormalRangeCases.length }} normal /
                {{ litigationCases.length }} total
              </div>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Case Group Statistics -->
    <v-card v-if="caseGroupStats.length > 0" outlined class="stats-card mb-3">
      <v-card-title class="pb-2">
        <span class="text-h6">Statistics by Case Group</span>
        <v-spacer></v-spacer>
        <v-btn-toggle v-model="caseGroupStatsView" mandatory>
          <v-btn small value="summary">Summary</v-btn>
          <v-btn small value="detailed">Detailed</v-btn>
        </v-btn-toggle>
      </v-card-title>

      <!-- Summary View -->
      <v-card-text v-if="caseGroupStatsView === 'summary'" class="pa-2">
        <v-row no-gutters>
          <v-col
            v-for="caseGroup in caseGroupStats"
            :key="caseGroup.name"
            cols="12"
            sm="6"
            md="4"
            lg="3"
            class="pa-2"
          >
            <v-card outlined class="case-group-stat-card">
              <v-card-text class="text-center pa-3">
                <div class="case-group-name">{{ caseGroup.name }}</div>
                <div class="case-group-metric">
                  <div class="metric-value primary--text">
                    {{ caseGroup.totalCases }}
                  </div>
                  <div class="metric-label">Total Cases</div>
                </div>
                <div class="case-group-metric">
                  <div class="metric-value success--text">
                    {{ formatMoney(caseGroup.averageFee) }}
                  </div>
                  <div class="metric-label">Overall Avg (>$2K)</div>
                </div>
                <v-divider class="my-2"></v-divider>
                <div class="litigation-breakdown">
                  <div class="litigation-row">
                    <div class="litigation-metric">
                      <div class="metric-value info--text text-body-2">
                        {{ formatMoney(caseGroup.preLitigationAverage) }}
                      </div>
                      <div class="metric-sublabel">Pre-Lit Avg</div>
                      <div class="metric-count">
                        {{ caseGroup.preLitigationQualifying }} qualifying
                      </div>
                      <div class="metric-sublabel mt-1">Normal</div>
                      <div class="metric-count">
                        {{
                          formatMoney(caseGroup.preLitigationNormalRangeAverage)
                        }}
                      </div>
                    </div>
                    <div class="litigation-metric">
                      <div class="metric-value error--text text-body-2">
                        {{ formatMoney(caseGroup.litigationAverage) }}
                      </div>
                      <div class="metric-sublabel">Litigation Avg</div>
                      <div class="metric-count">
                        {{ caseGroup.litigationQualifying }} qualifying
                      </div>
                      <div class="metric-sublabel mt-1">Normal</div>
                      <div class="metric-count">
                        {{
                          formatMoney(caseGroup.litigationNormalRangeAverage)
                        }}
                      </div>
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Detailed View -->
      <v-data-table
        v-if="caseGroupStatsView === 'detailed'"
        :headers="caseGroupHeaders"
        :items="caseGroupStats"
        class="elevation-0"
        :items-per-page="-1"
        :sort-by="['totalCases']"
        :sort-desc="[true]"
        hide-default-footer
      >
        <template v-slot:item.averageFee="{ item }">
          {{ formatMoney(item.averageFee) }}
        </template>
        <template v-slot:item.normalRangeAverage="{ item }">
          {{ formatMoney(item.normalRangeAverage) }}
        </template>
        <template v-slot:item.preLitigationAverage="{ item }">
          {{ formatMoney(item.preLitigationAverage) }}
        </template>
        <template v-slot:item.preLitigationNormalRangeAverage="{ item }">
          {{ formatMoney(item.preLitigationNormalRangeAverage) }}
        </template>
        <template v-slot:item.litigationAverage="{ item }">
          {{ formatMoney(item.litigationAverage) }}
        </template>
        <template v-slot:item.litigationNormalRangeAverage="{ item }">
          {{ formatMoney(item.litigationNormalRangeAverage) }}
        </template>
      </v-data-table>
    </v-card>

    <!-- Office Statistics -->
    <v-card v-if="officeStats.length > 0" outlined class="stats-card mb-3">
      <v-card-title class="pb-2">
        <span class="text-h6">Statistics by Office</span>
        <v-spacer></v-spacer>
        <v-btn-toggle v-model="officeStatsView" mandatory>
          <v-btn small value="summary">Summary</v-btn>
          <v-btn small value="detailed">Detailed</v-btn>
        </v-btn-toggle>
      </v-card-title>

      <!-- Summary View -->
      <v-card-text v-if="officeStatsView === 'summary'" class="pa-2">
        <v-row no-gutters>
          <v-col
            v-for="office in officeStats"
            :key="office.name"
            cols="12"
            sm="6"
            md="4"
            lg="3"
            class="pa-2"
          >
            <v-card outlined class="office-stat-card">
              <v-card-text class="text-center pa-3">
                <div class="office-name">{{ office.name }}</div>
                <div class="office-metric">
                  <div class="metric-value primary--text">
                    {{ office.totalCases }}
                  </div>
                  <div class="metric-label">Total Cases</div>
                </div>
                <div class="office-metric">
                  <div class="metric-value success--text">
                    {{ formatMoney(office.averageFee) }}
                  </div>
                  <div class="metric-label">Overall Avg (>$2K)</div>
                </div>
                <v-divider class="my-2"></v-divider>
                <div class="litigation-breakdown">
                  <div class="litigation-row">
                    <div class="litigation-metric">
                      <div class="metric-value info--text text-body-2">
                        {{ formatMoney(office.preLitigationAverage) }}
                      </div>
                      <div class="metric-sublabel">Pre-Lit Avg</div>
                      <div class="metric-count">
                        {{ office.preLitigationQualifying }} qualifying
                      </div>
                      <div class="metric-sublabel mt-1">Normal</div>
                      <div class="metric-count">
                        {{
                          formatMoney(office.preLitigationNormalRangeAverage)
                        }}
                      </div>
                    </div>
                    <div class="litigation-metric">
                      <div class="metric-value error--text text-body-2">
                        {{ formatMoney(office.litigationAverage) }}
                      </div>
                      <div class="metric-sublabel">Litigation Avg</div>
                      <div class="metric-count">
                        {{ office.litigationQualifying }} qualifying
                      </div>
                      <div class="metric-sublabel mt-1">Normal</div>
                      <div class="metric-count">
                        {{ formatMoney(office.litigationNormalRangeAverage) }}
                      </div>
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Detailed View -->
      <v-data-table
        v-if="officeStatsView === 'detailed'"
        :headers="officeHeaders"
        :items="officeStats"
        class="elevation-0"
        :items-per-page="-1"
        :sort-by="['totalCases']"
        :sort-desc="[true]"
        hide-default-footer
      >
        <template v-slot:item.averageFee="{ item }">
          {{ formatMoney(item.averageFee) }}
        </template>
        <template v-slot:item.normalRangeAverage="{ item }">
          {{ formatMoney(item.normalRangeAverage) }}
        </template>
        <template v-slot:item.preLitigationAverage="{ item }">
          {{ formatMoney(item.preLitigationAverage) }}
        </template>
        <template v-slot:item.preLitigationNormalRangeAverage="{ item }">
          {{ formatMoney(item.preLitigationNormalRangeAverage) }}
        </template>
        <template v-slot:item.litigationAverage="{ item }">
          {{ formatMoney(item.litigationAverage) }}
        </template>
        <template v-slot:item.litigationNormalRangeAverage="{ item }">
          {{ formatMoney(item.litigationNormalRangeAverage) }}
        </template>
      </v-data-table>
    </v-card>

    <!-- Summary Statistics Table -->
    <v-card
      v-if="summaryStats.length > 0"
      outlined
      class="summary-stats-card mb-3"
    >
      <v-card-title class="pb-2">
        <span class="text-h6">Summary Statistics</span>
        <v-spacer></v-spacer>
        <v-btn-toggle v-model="summaryStatsView" mandatory>
          <v-btn small value="caseGroup">By Case Group</v-btn>
          <v-btn small value="office">By Office</v-btn>
        </v-btn-toggle>
      </v-card-title>
      <v-data-table
        :headers="summaryHeaders"
        :items="summaryStats"
        class="elevation-0"
        :items-per-page="-1"
        :sort-by="['totalCases']"
        :sort-desc="[true]"
        hide-default-footer
      >
        <template v-slot:item.averageFee="{ item }">
          {{ formatMoney(item.averageFee) }}
        </template>
        <template v-slot:item.normalRangeAverage="{ item }">
          {{ formatMoney(item.normalRangeAverage) }}
        </template>
        <template v-slot:item.preLitigationAverage="{ item }">
          {{ formatMoney(item.preLitigationAverage) }}
        </template>
        <template v-slot:item.preLitigationNormalRangeAverage="{ item }">
          {{ formatMoney(item.preLitigationNormalRangeAverage) }}
        </template>
        <template v-slot:item.litigationAverage="{ item }">
          {{ formatMoney(item.litigationAverage) }}
        </template>
        <template v-slot:item.litigationNormalRangeAverage="{ item }">
          {{ formatMoney(item.litigationNormalRangeAverage) }}
        </template>
        <template v-slot:item.qualifyingCases="{ item }">
          <span class="text-body-2">{{ item.qualifyingCases }}</span>
        </template>
        <template v-slot:item.normalRangeCases="{ item }">
          <span class="text-body-2">{{ item.normalRangeCases }}</span>
        </template>
        <template v-slot:item.preLitigationQualifying="{ item }">
          <span class="text-body-2">{{ item.preLitigationQualifying }}</span>
        </template>
        <template v-slot:item.preLitigationNormalRangeCases="{ item }">
          <span class="text-body-2">{{
            item.preLitigationNormalRangeCases
          }}</span>
        </template>
        <template v-slot:item.litigationQualifying="{ item }">
          <span class="text-body-2">{{ item.litigationQualifying }}</span>
        </template>
        <template v-slot:item.litigationNormalRangeCases="{ item }">
          <span class="text-body-2">{{ item.litigationNormalRangeCases }}</span>
        </template>
      </v-data-table>
    </v-card>

    <!-- Referral Fees Paid -->
    <v-card
      v-if="referralFeesPaid.length > 0"
      outlined
      class="referral-fees-card mb-3"
    >
      <v-card-title class="pb-2">
        <span class="text-h6">Referral Fees Paid</span>
      </v-card-title>
      <v-card-text class="pa-2">
        <v-row>
          <v-col cols="12" md="3">
            <div class="referral-fees-total">
              <div class="stat-label">Total Paid</div>
              <div class="stat-number warning--text">
                {{ referralFeesPaidTotal }}
              </div>
            </div>
          </v-col>
          <v-col cols="12" md="9">
            <v-data-table
              :headers="referralFeesPaidHeaders"
              :items="referralFeesPaid"
              class="elevation-0 referral-fees-table"
              :items-per-page="5"
              :sort-by="['fee']"
              :sort-desc="[true]"
              dense
            >
              <template v-slot:item.caseGroup="{ item }">
                {{ item.caseGroup || "Unspecified" }}
              </template>
              <template v-slot:item.fee="{ item }">
                {{ formatMoney(item.fee) }}
              </template>
              <template v-slot:item.percentage="{ item }">
                {{ item.percentage }}
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Attorney Analytics Table -->
    <v-card
      v-if="attorneyAnalytics.length > 0"
      outlined
      class="analytics-card mb-3"
    >
      <v-card-title class="pb-2">
        <span class="text-h6">Attorney Performance Analytics</span>
      </v-card-title>
      <v-data-table
        :headers="attorneyHeaders"
        :items="attorneyAnalytics"
        class="elevation-0"
        :footer-props="{
          'items-per-page-options': [5, 10, -1]
        }"
        :sort-by="['averageFee']"
        :sort-desc="[true]"
      >
        <template v-slot:item.averageFee="{ item }">
          {{ formatMoney(item.averageFee) }}
        </template>
        <template v-slot:item.normalRangeAverage="{ item }">
          {{ formatMoney(item.normalRangeAverage) }}
        </template>
        <template v-slot:item.qualifyingCases="{ item }">
          <span class="text-body-2">{{ item.qualifyingCases }}</span>
        </template>
        <template v-slot:item.normalRangeCases="{ item }">
          <span class="text-body-2">{{ item.normalRangeCases }}</span>
        </template>
        <template v-slot:item.policyLimitsExhaustedPercentage="{ item }">
          {{ item.policyLimitsExhaustedPercentage }}
        </template>
      </v-data-table>
    </v-card>

    <!-- Fee Distribution Analysis -->
    <v-card v-if="feeDistribution.length > 0" outlined class="stats-card mb-3">
      <v-card-title class="pb-2">
        <span class="text-h6">Fee Distribution Analysis</span>
        <v-spacer></v-spacer>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon small v-bind="attrs" v-on="on"
              >mdi-information-outline</v-icon
            >
          </template>
          <span
            >Shows how attorney fees are distributed across cases. The
            cumulative column reveals what percentage of cases account for the
            majority of revenue (Pareto principle).</span
          >
        </v-tooltip>
      </v-card-title>

      <!-- Pareto Highlight -->
      <v-card-text v-if="paretoSummary" class="pt-0 pb-2">
        <v-alert type="info" dense outlined class="mb-0">
          <strong>{{ paretoSummary.topCasesPercent }}%</strong> of cases ({{
            paretoSummary.topCasesCount
          }}
          cases with fees above {{ formatMoney(paretoSummary.threshold) }})
          account for <strong>{{ paretoSummary.revenuePercent }}%</strong> of
          total attorney fees.
        </v-alert>
      </v-card-text>

      <v-data-table
        :headers="feeDistributionHeaders"
        :items="feeDistribution"
        class="elevation-0"
        :items-per-page="-1"
        hide-default-footer
      >
        <template v-slot:item.totalFees="{ item }">
          {{ formatMoney(item.totalFees) }}
        </template>
        <template v-slot:item.averageFee="{ item }">
          {{ formatMoney(item.averageFee) }}
        </template>
        <template v-slot:item.casesPercent="{ item }">
          {{ item.casesPercent }}%
        </template>
        <template v-slot:item.revenuePercent="{ item }">
          {{ item.revenuePercent }}%
        </template>
        <template v-slot:item.cumulativeRevenuePercent="{ item }">
          <span
            :class="{
              'font-weight-bold': item.cumulativeRevenuePercent >= 80
            }"
          >
            {{ item.cumulativeRevenuePercent }}%
          </span>
        </template>
      </v-data-table>
    </v-card>

    <!-- Search and Data Table -->
    <v-card class="elevation-1">
      <v-card-title class="pb-2">
        <span class="text-h6">Settled Cases</span>
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          append-icon="mdi-search"
          label="Search"
          hide-details
          single-line
          style="max-width: 300px;"
        ></v-text-field>
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="settledCases"
        class="elevation-0"
        :footer-props="{
          'items-per-page-options': [50, 100, -1]
        }"
        :sort-by="['dateSettled']"
        :sort-desc="[true]"
        :search="search"
        :loading="$apollo.loading"
      >
        <template v-slot:item.caseId="props">
          <a
            class="go-to-case-link mr-2"
            target="_blank"
            :href="
              `https://cs-web.airdesksolutions.com/SA/CaseSummary.aspx?CaseID=${props.item.caseId}`
            "
          >
            <v-icon size="15">mdi-open-in-new</v-icon>
          </a>
        </template>
        <template v-slot:item.totalFees="props">
          {{ formatMoney(props.item.totalFees) }}
        </template>
        <template v-slot:item.totalSettlement="props">
          {{ formatMoney(props.item.totalSettlement) }}
        </template>
        <template v-slot:item.policyLimitsExhausted="props">
          <v-chip
            x-small
            :color="isPolicyLimitsExhausted(props.item) ? 'warning' : 'grey'"
            :text-color="isPolicyLimitsExhausted(props.item) ? 'white' : ''"
          >
            {{ isPolicyLimitsExhausted(props.item) ? "Yes" : "No" }}
          </v-chip>
        </template>
        <template v-slot:item.dateSettled="props">
          {{ formatDate(props.item.dateSettled) }}
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import gql from "graphql-tag";
import { mapGetters } from "vuex";
import accounting from "accounting";

export default {
  data() {
    return {
      search: "",
      officeStatsView: "summary",
      caseGroupStatsView: "summary",
      summaryStatsView: "caseGroup"
    };
  },
  apollo: {
    settledCases: {
      query: gql`
        query($startDate: String!, $endDate: String!) {
          fullySettledCasesInPeriod(startDate: $startDate, endDate: $endDate) {
            caseId
            clientName
            attorneyName
            paralegalName
            office
            caseGroup
            litigationStatus
            totalFees
            totalSettlement
            policyLimitsExhausted
            dateSettled
          }
        }
      `,
      variables() {
        return {
          startDate: this.startDate,
          endDate: this.endDate
        };
      },
      fetchPolicy: "no-cache",
      skip() {
        return this.startDate === null || this.endDate === null;
      },
      update(data) {
        return data.fullySettledCasesInPeriod || [];
      }
    },
    organization: {
      query: gql`
        query($id: ID!, $startDate: String!, $endDate: String!) {
          organization(id: $id) {
            id
            sa_referral_fees_paid_by_case_group(
              startDate: $startDate
              endDate: $endDate
            ) {
              caseGroup
              fee
            }
          }
        }
      `,
      variables() {
        return {
          id: this.$route.params.id,
          startDate: this.startDate,
          endDate: this.endDate
        };
      },
      fetchPolicy: "no-cache",
      skip() {
        return this.startDate === null || this.endDate === null;
      }
    }
  },
  computed: {
    ...mapGetters(["startDate", "endDate"]),
    headers() {
      return [
        { text: "Action", value: "caseId" },
        { text: "Client", value: "clientName" },
        { text: "Attorney", value: "attorneyName" },
        { text: "Paralegal", value: "paralegalName" },
        { text: "Office", value: "office" },
        { text: "Case Group", value: "caseGroup" },
        { text: "Litigation Status", value: "litigationStatus" },
        { text: "Total Fees", value: "totalFees" },
        {
          text: "Total Settlement",
          value: "totalSettlement",
          sort: (a, b) => parseFloat(a) - parseFloat(b)
        },
        { text: "Policy Limits Exhausted", value: "policyLimitsExhausted" },
        { text: "Date Settled", value: "dateSettled" }
      ];
    },
    referralFeesPaidHeaders() {
      return [
        { text: "Case Group", value: "caseGroup" },
        { text: "Fee Paid", value: "fee" },
        { text: "Share", value: "percentage" }
      ];
    },
    referralFeesPaid() {
      if (!this.organization) return [];
      const fees = this.organization.sa_referral_fees_paid_by_case_group || [];
      const total = fees.reduce((sum, fee) => sum + fee.fee, 0);

      return fees.map(fee => {
        return {
          ...fee,
          percentage:
            total > 0 ? ((fee.fee / total) * 100).toFixed(1) + "%" : "0%"
        };
      });
    },
    referralFeesPaidTotal() {
      const total = this.referralFeesPaid.reduce((sum, fee) => {
        return sum + fee.fee;
      }, 0);
      return this.formatMoney(total);
    },
    totalSettledCases() {
      if (!this.settledCases || !Array.isArray(this.settledCases)) return 0;
      return this.settledCases.length;
    },
    policyLimitsExhaustedCases() {
      if (!this.settledCases || !Array.isArray(this.settledCases)) return [];
      return this.settledCases.filter(case_ =>
        this.isPolicyLimitsExhausted(case_)
      );
    },
    policyLimitsExhaustedCount() {
      return this.policyLimitsExhaustedCases.length;
    },
    policyLimitsExhaustedPercentage() {
      return this.formatPercent(
        this.policyLimitsExhaustedCount,
        this.totalSettledCases
      );
    },
    totalFees() {
      if (!this.settledCases || !Array.isArray(this.settledCases)) {
        return 0;
      }
      return this.settledCases.reduce((sum, case_) => {
        return sum + this.caseFee(case_);
      }, 0);
    },

    qualifyingCases() {
      if (!this.settledCases || !Array.isArray(this.settledCases)) return [];
      return this.settledCases.filter(case_ => {
        const fees = this.caseFee(case_);
        return fees > 2000;
      });
    },
    qualifyingCasesCount() {
      return this.qualifyingCases.length;
    },
    averageFee() {
      if (!this.qualifyingCases || this.qualifyingCases.length === 0) return 0;
      const totalQualifyingFees = this.qualifyingCases.reduce((sum, case_) => {
        return sum + this.caseFee(case_);
      }, 0);
      return totalQualifyingFees / this.qualifyingCases.length;
    },
    averageFeeFormatted() {
      return this.formatMoney(this.averageFee);
    },
    normalRangeCases() {
      if (!this.settledCases || !Array.isArray(this.settledCases)) return [];
      return this.settledCases.filter(case_ => {
        const fees = this.caseFee(case_);
        return fees > 2000 && fees <= 1000000;
      });
    },
    normalRangeCasesCount() {
      return this.normalRangeCases.length;
    },
    normalRangeAverageFee() {
      if (!this.normalRangeCases || this.normalRangeCases.length === 0)
        return 0;
      const totalNormalRangeFees = this.normalRangeCases.reduce(
        (sum, case_) => {
          return sum + this.caseFee(case_);
        },
        0
      );
      return totalNormalRangeFees / this.normalRangeCases.length;
    },
    normalRangeAverageFeeFormatted() {
      return this.formatMoney(this.normalRangeAverageFee);
    },
    // Litigation Status Statistics
    preLitigationCases() {
      if (!this.settledCases || !Array.isArray(this.settledCases)) return [];
      return this.settledCases.filter(case_ => {
        const status = case_.litigationStatus || "";
        return (
          status.toLowerCase().includes("pre-litigation") ||
          status.toLowerCase().includes("prelitigation")
        );
      });
    },
    litigationCases() {
      if (!this.settledCases || !Array.isArray(this.settledCases)) return [];
      return this.settledCases.filter(case_ => {
        const status = case_.litigationStatus || "";
        return (
          status.toLowerCase().includes("litigation") &&
          !status.toLowerCase().includes("pre-litigation") &&
          !status.toLowerCase().includes("prelitigation")
        );
      });
    },
    preLitigationQualifyingCases() {
      return this.preLitigationCases.filter(case_ => {
        const fees = this.caseFee(case_);
        return fees > 2000;
      });
    },
    preLitigationNormalRangeCases() {
      return this.preLitigationCases.filter(case_ => {
        const fees = this.caseFee(case_);
        return fees > 2000 && fees <= 1000000;
      });
    },
    litigationQualifyingCases() {
      return this.litigationCases.filter(case_ => {
        const fees = this.caseFee(case_);
        return fees > 2000;
      });
    },
    litigationNormalRangeCases() {
      return this.litigationCases.filter(case_ => {
        const fees = this.caseFee(case_);
        return fees > 2000 && fees <= 1000000;
      });
    },
    preLitigationAverageFee() {
      if (
        !this.preLitigationQualifyingCases ||
        this.preLitigationQualifyingCases.length === 0
      )
        return 0;
      const total = this.preLitigationQualifyingCases.reduce((sum, case_) => {
        return sum + this.caseFee(case_);
      }, 0);
      return total / this.preLitigationQualifyingCases.length;
    },
    litigationAverageFee() {
      if (
        !this.litigationQualifyingCases ||
        this.litigationQualifyingCases.length === 0
      )
        return 0;
      const total = this.litigationQualifyingCases.reduce((sum, case_) => {
        return sum + this.caseFee(case_);
      }, 0);
      return total / this.litigationQualifyingCases.length;
    },
    preLitigationNormalRangeAverageFee() {
      if (
        !this.preLitigationNormalRangeCases ||
        this.preLitigationNormalRangeCases.length === 0
      )
        return 0;
      const total = this.preLitigationNormalRangeCases.reduce((sum, case_) => {
        return sum + this.caseFee(case_);
      }, 0);
      return total / this.preLitigationNormalRangeCases.length;
    },
    litigationNormalRangeAverageFee() {
      if (
        !this.litigationNormalRangeCases ||
        this.litigationNormalRangeCases.length === 0
      )
        return 0;
      const total = this.litigationNormalRangeCases.reduce((sum, case_) => {
        return sum + this.caseFee(case_);
      }, 0);
      return total / this.litigationNormalRangeCases.length;
    },
    preLitigationAverageFeeFormatted() {
      return this.formatMoney(this.preLitigationAverageFee);
    },
    litigationAverageFeeFormatted() {
      return this.formatMoney(this.litigationAverageFee);
    },
    preLitigationNormalRangeAverageFeeFormatted() {
      return this.formatMoney(this.preLitigationNormalRangeAverageFee);
    },
    litigationNormalRangeAverageFeeFormatted() {
      return this.formatMoney(this.litigationNormalRangeAverageFee);
    },
    feeDistributionHeaders() {
      return [
        { text: "Fee Range", value: "range" },
        { text: "Cases", value: "count" },
        { text: "% of Cases", value: "casesPercent" },
        { text: "Total Fees", value: "totalFees" },
        { text: "Average Fee", value: "averageFee" },
        { text: "% of Revenue", value: "revenuePercent" },
        { text: "Cumulative Revenue %", value: "cumulativeRevenuePercent" }
      ];
    },
    feeDistribution() {
      if (
        !this.settledCases ||
        !Array.isArray(this.settledCases) ||
        this.settledCases.length === 0
      )
        return [];

      const thresholds = [
        { min: 0, max: 5000, label: "Under $5,000" },
        { min: 5000, max: 10000, label: "$5,000 - $10,000" },
        { min: 10000, max: 25000, label: "$10,000 - $25,000" },
        { min: 25000, max: 50000, label: "$25,000 - $50,000" },
        { min: 50000, max: 100000, label: "$50,000 - $100,000" },
        { min: 100000, max: 250000, label: "$100,000 - $250,000" },
        { min: 250000, max: 500000, label: "$250,000 - $500,000" },
        { min: 500000, max: 1000000, label: "$500,000 - $1,000,000" },
        { min: 1000000, max: Infinity, label: "Over $1,000,000" }
      ];

      const totalCases = this.settledCases.length;

      // Build buckets (highest to lowest so cumulative goes top-down)
      const buckets = thresholds
        .map(t => {
          const cases = this.settledCases.filter(c => {
            const fees = this.caseFee(c);
            return fees >= t.min && fees < t.max;
          });
          const totalFees = cases.reduce((sum, c) => sum + this.caseFee(c), 0);
          return {
            range: t.label,
            count: cases.length,
            totalFees,
            averageFee: cases.length > 0 ? totalFees / cases.length : 0,
            sortOrder: t.min
          };
        })
        .filter(b => b.count > 0);

      // Sort highest to lowest
      buckets.sort((a, b) => b.sortOrder - a.sortOrder);

      // Calculate percentages and cumulative
      let cumulativeRevenue = 0;
      return buckets.map(b => {
        cumulativeRevenue += b.totalFees;
        return {
          ...b,
          casesPercent: ((b.count / totalCases) * 100).toFixed(1),
          revenuePercent: ((b.totalFees / this.totalFees) * 100).toFixed(1),
          cumulativeRevenuePercent: (
            (cumulativeRevenue / this.totalFees) *
            100
          ).toFixed(1)
        };
      });
    },
    paretoSummary() {
      if (!this.feeDistribution || this.feeDistribution.length === 0)
        return null;

      // Find the bucket where cumulative revenue first reaches or exceeds 80%
      let cumulativeCases = 0;
      for (const bucket of this.feeDistribution) {
        cumulativeCases += bucket.count;
        if (parseFloat(bucket.cumulativeRevenuePercent) >= 80) {
          const totalCases = this.settledCases.length;
          return {
            topCasesPercent: ((cumulativeCases / totalCases) * 100).toFixed(1),
            topCasesCount: cumulativeCases,
            revenuePercent: bucket.cumulativeRevenuePercent,
            threshold: bucket.sortOrder
          };
        }
      }
      return null;
    },
    summaryHeaders() {
      const nameColumn =
        this.summaryStatsView === "caseGroup"
          ? { text: "Case Group", value: "name" }
          : { text: "Office", value: "name" };

      return [
        nameColumn,
        { text: "Total Cases", value: "totalCases" },
        { text: "Average Fee (>$2K)", value: "averageFee" },
        { text: "Qualifying Cases", value: "qualifyingCases" },
        { text: "Normal Range Average ($2K-$1M)", value: "normalRangeAverage" },
        { text: "Normal Range Cases", value: "normalRangeCases" },
        {
          text: "Pre-Litigation Average (>$2K)",
          value: "preLitigationAverage"
        },
        {
          text: "Pre-Lit Normal Range Avg",
          value: "preLitigationNormalRangeAverage"
        },
        {
          text: "Pre-Lit Qualifying Cases",
          value: "preLitigationQualifying"
        },
        {
          text: "Pre-Lit Normal Range Cases",
          value: "preLitigationNormalRangeCases"
        },
        { text: "Litigation Average (>$2K)", value: "litigationAverage" },
        {
          text: "Litigation Normal Range Avg",
          value: "litigationNormalRangeAverage"
        },
        {
          text: "Litigation Qualifying Cases",
          value: "litigationQualifying"
        },
        {
          text: "Litigation Normal Range Cases",
          value: "litigationNormalRangeCases"
        }
      ];
    },
    summaryStats() {
      if (!this.settledCases || !Array.isArray(this.settledCases)) return [];

      if (this.summaryStatsView === "caseGroup") {
        return this.caseGroupStats;
      } else {
        return this.officeStats;
      }
    },
    attorneyHeaders() {
      return [
        { text: "Attorney", value: "name" },
        { text: "Total Cases", value: "totalCases" },
        { text: "Average Fee (>$2K)", value: "averageFee" },
        { text: "Cases", value: "qualifyingCases" },
        {
          text: "Normal Range Average ($2K-$1M)",
          value: "normalRangeAverage"
        },
        { text: "Cases", value: "normalRangeCases" },
        {
          text: "Policy Limits Exhausted",
          value: "policyLimitsExhaustedCount"
        },
        {
          text: "Policy Limits %",
          value: "policyLimitsExhaustedPercentage",
          // Values are formatted strings like "45.5%"; parse to numbers so the
          // column sorts numerically instead of lexicographically.
          sort: (a, b) => parseFloat(a) - parseFloat(b)
        }
      ];
    },
    attorneyAnalytics() {
      if (!this.settledCases || !Array.isArray(this.settledCases)) return [];

      // Group cases by attorney
      const casesByAttorney = {};
      this.settledCases.forEach(case_ => {
        const attorney = case_.attorneyName || "Unknown";
        if (!casesByAttorney[attorney]) {
          casesByAttorney[attorney] = [];
        }
        casesByAttorney[attorney].push(case_);
      });

      // Calculate analytics for each attorney
      return Object.keys(casesByAttorney)
        .map(attorneyName => {
          const cases = casesByAttorney[attorneyName];

          // Cases with fees > $2,000
          const qualifyingCases = cases.filter(case_ => {
            const fees = this.caseFee(case_);
            return fees > 2000;
          });

          // Cases with fees $2,000 - $1,000,000
          const normalRangeCases = cases.filter(case_ => {
            const fees = this.caseFee(case_);
            return fees > 2000 && fees <= 1000000;
          });

          // Calculate average fee (>$2K)
          const averageFee =
            qualifyingCases.length > 0
              ? qualifyingCases.reduce(
                  (sum, case_) => sum + this.caseFee(case_),
                  0
                ) / qualifyingCases.length
              : 0;

          // Calculate normal range average
          const normalRangeAverage =
            normalRangeCases.length > 0
              ? normalRangeCases.reduce(
                  (sum, case_) => sum + this.caseFee(case_),
                  0
                ) / normalRangeCases.length
              : 0;
          const policyLimitsExhaustedCount = cases.filter(case_ =>
            this.isPolicyLimitsExhausted(case_)
          ).length;

          return {
            name: attorneyName,
            totalCases: cases.length,
            qualifyingCases: qualifyingCases.length,
            normalRangeCases: normalRangeCases.length,
            policyLimitsExhaustedCount,
            policyLimitsExhaustedPercentage: this.formatPercent(
              policyLimitsExhaustedCount,
              cases.length
            ),
            averageFee,
            normalRangeAverage
          };
        })
        .sort((a, b) => b.averageFee - a.averageFee); // Sort by average fee descending
    },
    officeHeaders() {
      return [
        { text: "Office", value: "name" },
        { text: "Total Cases", value: "totalCases" },
        { text: "Average Fee (>$2K)", value: "averageFee" },
        { text: "Qualifying Cases", value: "qualifyingCases" },
        {
          text: "Normal Range Average ($2K-$1M)",
          value: "normalRangeAverage"
        },
        { text: "Normal Range Cases", value: "normalRangeCases" },
        {
          text: "Pre-Litigation Average (>$2K)",
          value: "preLitigationAverage"
        },
        {
          text: "Pre-Lit Normal Range Avg",
          value: "preLitigationNormalRangeAverage"
        },
        {
          text: "Pre-Lit Qualifying Cases",
          value: "preLitigationQualifying"
        },
        {
          text: "Pre-Lit Normal Range Cases",
          value: "preLitigationNormalRangeCases"
        },
        { text: "Litigation Average (>$2K)", value: "litigationAverage" },
        {
          text: "Litigation Normal Range Avg",
          value: "litigationNormalRangeAverage"
        },
        {
          text: "Litigation Qualifying Cases",
          value: "litigationQualifying"
        },
        {
          text: "Litigation Normal Range Cases",
          value: "litigationNormalRangeCases"
        }
      ];
    },
    caseGroupHeaders() {
      return [
        { text: "Case Group", value: "name" },
        { text: "Total Cases", value: "totalCases" },
        { text: "Average Fee (>$2K)", value: "averageFee" },
        { text: "Qualifying Cases", value: "qualifyingCases" },
        {
          text: "Normal Range Average ($2K-$1M)",
          value: "normalRangeAverage"
        },
        { text: "Normal Range Cases", value: "normalRangeCases" },
        {
          text: "Pre-Litigation Average (>$2K)",
          value: "preLitigationAverage"
        },
        {
          text: "Pre-Lit Normal Range Avg",
          value: "preLitigationNormalRangeAverage"
        },
        {
          text: "Pre-Lit Qualifying Cases",
          value: "preLitigationQualifying"
        },
        {
          text: "Pre-Lit Normal Range Cases",
          value: "preLitigationNormalRangeCases"
        },
        { text: "Litigation Average (>$2K)", value: "litigationAverage" },
        {
          text: "Litigation Normal Range Avg",
          value: "litigationNormalRangeAverage"
        },
        {
          text: "Litigation Qualifying Cases",
          value: "litigationQualifying"
        },
        {
          text: "Litigation Normal Range Cases",
          value: "litigationNormalRangeCases"
        }
      ];
    },
    officeStats() {
      if (!this.settledCases || !Array.isArray(this.settledCases)) return [];

      // Group cases by office
      const casesByOffice = {};
      this.settledCases.forEach(case_ => {
        const office = this.mapOfficeName(case_.office || "Other");
        if (!casesByOffice[office]) {
          casesByOffice[office] = [];
        }
        casesByOffice[office].push(case_);
      });

      // Calculate statistics for each office
      return Object.keys(casesByOffice)
        .map(officeName => {
          const cases = casesByOffice[officeName];

          // Cases with fees > $2,000
          const qualifyingCases = cases.filter(case_ => {
            const fees = this.caseFee(case_);
            return fees > 2000;
          });

          // Cases with fees $2,000 - $1,000,000
          const normalRangeCases = cases.filter(case_ => {
            const fees = this.caseFee(case_);
            return fees > 2000 && fees <= 1000000;
          });

          // Calculate average fee (>$2K)
          const averageFee =
            qualifyingCases.length > 0
              ? qualifyingCases.reduce(
                  (sum, case_) => sum + this.caseFee(case_),
                  0
                ) / qualifyingCases.length
              : 0;

          // Calculate normal range average
          const normalRangeAverage =
            normalRangeCases.length > 0
              ? normalRangeCases.reduce(
                  (sum, case_) => sum + this.caseFee(case_),
                  0
                ) / normalRangeCases.length
              : 0;

          // Litigation breakdown
          const preLitigationCases = cases.filter(case_ => {
            const status = case_.litigationStatus || "";
            return (
              status.toLowerCase().includes("pre-litigation") ||
              status.toLowerCase().includes("prelitigation")
            );
          });

          const litigationCases = cases.filter(case_ => {
            const status = case_.litigationStatus || "";
            return (
              status.toLowerCase().includes("litigation") &&
              !status.toLowerCase().includes("pre-litigation") &&
              !status.toLowerCase().includes("prelitigation")
            );
          });

          const preLitigationQualifying = preLitigationCases.filter(case_ => {
            const fees = this.caseFee(case_);
            return fees > 2000;
          });

          const preLitigationNormalRange = preLitigationCases.filter(case_ => {
            const fees = this.caseFee(case_);
            return fees > 2000 && fees <= 1000000;
          });

          const litigationQualifying = litigationCases.filter(case_ => {
            const fees = this.caseFee(case_);
            return fees > 2000;
          });

          const litigationNormalRange = litigationCases.filter(case_ => {
            const fees = this.caseFee(case_);
            return fees > 2000 && fees <= 1000000;
          });

          const preLitigationAverage =
            preLitigationQualifying.length > 0
              ? preLitigationQualifying.reduce(
                  (sum, case_) => sum + this.caseFee(case_),
                  0
                ) / preLitigationQualifying.length
              : 0;

          const litigationAverage =
            litigationQualifying.length > 0
              ? litigationQualifying.reduce(
                  (sum, case_) => sum + this.caseFee(case_),
                  0
                ) / litigationQualifying.length
              : 0;

          const preLitigationNormalRangeAverage =
            preLitigationNormalRange.length > 0
              ? preLitigationNormalRange.reduce(
                  (sum, case_) => sum + this.caseFee(case_),
                  0
                ) / preLitigationNormalRange.length
              : 0;

          const litigationNormalRangeAverage =
            litigationNormalRange.length > 0
              ? litigationNormalRange.reduce(
                  (sum, case_) => sum + this.caseFee(case_),
                  0
                ) / litigationNormalRange.length
              : 0;

          return {
            name: officeName,
            totalCases: cases.length,
            qualifyingCases: qualifyingCases.length,
            normalRangeCases: normalRangeCases.length,
            averageFee,
            normalRangeAverage,
            preLitigationCases: preLitigationCases.length,
            litigationCases: litigationCases.length,
            preLitigationQualifying: preLitigationQualifying.length,
            litigationQualifying: litigationQualifying.length,
            preLitigationNormalRangeCases: preLitigationNormalRange.length,
            litigationNormalRangeCases: litigationNormalRange.length,
            preLitigationAverage,
            litigationAverage,
            preLitigationNormalRangeAverage,
            litigationNormalRangeAverage
          };
        })
        .sort((a, b) => b.totalCases - a.totalCases); // Sort by total cases descending
    },
    caseGroupStats() {
      if (!this.settledCases || !Array.isArray(this.settledCases)) return [];

      // Group cases by case group
      const casesByCaseGroup = {};
      this.settledCases.forEach(case_ => {
        const caseGroup = case_.caseGroup || "Other";
        if (!casesByCaseGroup[caseGroup]) {
          casesByCaseGroup[caseGroup] = [];
        }
        casesByCaseGroup[caseGroup].push(case_);
      });

      // Calculate statistics for each case group
      return Object.keys(casesByCaseGroup)
        .map(caseGroupName => {
          const cases = casesByCaseGroup[caseGroupName];

          // Cases with fees > $2,000
          const qualifyingCases = cases.filter(case_ => {
            const fees = this.caseFee(case_);
            return fees > 2000;
          });

          // Cases with fees $2,000 - $1,000,000
          const normalRangeCases = cases.filter(case_ => {
            const fees = this.caseFee(case_);
            return fees > 2000 && fees <= 1000000;
          });

          // Calculate average fee (>$2K)
          const averageFee =
            qualifyingCases.length > 0
              ? qualifyingCases.reduce(
                  (sum, case_) => sum + this.caseFee(case_),
                  0
                ) / qualifyingCases.length
              : 0;

          // Calculate normal range average
          const normalRangeAverage =
            normalRangeCases.length > 0
              ? normalRangeCases.reduce(
                  (sum, case_) => sum + this.caseFee(case_),
                  0
                ) / normalRangeCases.length
              : 0;

          // Litigation breakdown
          const preLitigationCases = cases.filter(case_ => {
            const status = case_.litigationStatus || "";
            return (
              status.toLowerCase().includes("pre-litigation") ||
              status.toLowerCase().includes("prelitigation")
            );
          });

          const litigationCases = cases.filter(case_ => {
            const status = case_.litigationStatus || "";
            return (
              status.toLowerCase().includes("litigation") &&
              !status.toLowerCase().includes("pre-litigation") &&
              !status.toLowerCase().includes("prelitigation")
            );
          });

          const preLitigationQualifying = preLitigationCases.filter(case_ => {
            const fees = this.caseFee(case_);
            return fees > 2000;
          });

          const preLitigationNormalRange = preLitigationCases.filter(case_ => {
            const fees = this.caseFee(case_);
            return fees > 2000 && fees <= 1000000;
          });

          const litigationQualifying = litigationCases.filter(case_ => {
            const fees = this.caseFee(case_);
            return fees > 2000;
          });

          const litigationNormalRange = litigationCases.filter(case_ => {
            const fees = this.caseFee(case_);
            return fees > 2000 && fees <= 1000000;
          });

          const preLitigationAverage =
            preLitigationQualifying.length > 0
              ? preLitigationQualifying.reduce(
                  (sum, case_) => sum + this.caseFee(case_),
                  0
                ) / preLitigationQualifying.length
              : 0;

          const litigationAverage =
            litigationQualifying.length > 0
              ? litigationQualifying.reduce(
                  (sum, case_) => sum + this.caseFee(case_),
                  0
                ) / litigationQualifying.length
              : 0;

          const preLitigationNormalRangeAverage =
            preLitigationNormalRange.length > 0
              ? preLitigationNormalRange.reduce(
                  (sum, case_) => sum + this.caseFee(case_),
                  0
                ) / preLitigationNormalRange.length
              : 0;

          const litigationNormalRangeAverage =
            litigationNormalRange.length > 0
              ? litigationNormalRange.reduce(
                  (sum, case_) => sum + this.caseFee(case_),
                  0
                ) / litigationNormalRange.length
              : 0;

          return {
            name: caseGroupName,
            totalCases: cases.length,
            qualifyingCases: qualifyingCases.length,
            normalRangeCases: normalRangeCases.length,
            averageFee,
            normalRangeAverage,
            preLitigationCases: preLitigationCases.length,
            litigationCases: litigationCases.length,
            preLitigationQualifying: preLitigationQualifying.length,
            litigationQualifying: litigationQualifying.length,
            preLitigationNormalRangeCases: preLitigationNormalRange.length,
            litigationNormalRangeCases: litigationNormalRange.length,
            preLitigationAverage,
            litigationAverage,
            preLitigationNormalRangeAverage,
            litigationNormalRangeAverage
          };
        })
        .sort((a, b) => b.totalCases - a.totalCases); // Sort by total cases descending
    }
  },

  methods: {
    caseFee(case_) {
      return accounting.unformat(case_ && case_.totalFees) || 0;
    },
    isPolicyLimitsExhausted(case_) {
      if (!case_) return false;
      return case_.policyLimitsExhausted === true;
    },
    formatPercent(numerator, denominator) {
      if (!denominator) return "0.0%";
      return ((numerator / denominator) * 100).toFixed(1) + "%";
    },
    formatMoney(val) {
      return accounting.formatMoney(val);
    },
    mapOfficeName(officeName) {
      // Map office names to shorter versions
      const officeMapping = {
        "(1 - UT Main) Sandy Office": "Utah",
        "(3 - ID Main) Meridian Office": "Boise",
        "(2 - WA Main) Spokane Valley Office": "Spokane",
        "Spokane Valley": "Spokane",
        Sandy: "Utah",
        Meridian: "Boise"
      };

      return officeMapping[officeName] || officeName;
    },
    formatDate(dateString) {
      if (!dateString) return "";

      // Handle date string - assume it's already in YYYY-MM-DD format from API
      if (
        typeof dateString === "string" &&
        dateString.match(/^\d{4}-\d{2}-\d{2}/)
      ) {
        // If it's already in YYYY-MM-DD format, just return the date part
        return dateString.split("T")[0]; // Remove time part if present
      }

      // Fallback: try to parse as Date and format
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString; // Return original if can't parse
      }

      // Format as YYYY-MM-DD
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const day = date
        .getDate()
        .toString()
        .padStart(2, "0");

      return `${year}-${month}-${day}`;
    }
  }
};
</script>

<style scoped>
/* Container Styling */
.v-container {
  width: 100% !important;
  max-width: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
}

.v-card {
  width: 100% !important;
}

.v-row {
  margin: 0 !important;
}

/* Compact Statistics Styling */
.stats-card {
  background: #ffffff;
  border: 1px solid #e0e0e0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.stats-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: space-between;
  align-items: stretch;
}

.stat-item {
  flex: 1;
  min-width: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}

@media (max-width: 960px) {
  .stats-grid {
    flex-direction: column;
    gap: 12px;
  }

  .stat-item {
    min-width: auto;
  }
}

@media (max-width: 1200px) and (min-width: 961px) {
  .stat-item:nth-child(4),
  .stat-item:nth-child(5) {
    flex-basis: calc(50% - 8px);
  }
}

.compact-stat {
  padding: 12px;
}

.stat-number {
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 14px;
  font-weight: 500;
  color: #666;
  line-height: 1.3;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-subtitle {
  font-size: 12px;
  color: #888;
  margin-top: 2px;
  font-style: italic;
}

.go-to-case-link {
  text-decoration: none;
}

/* Summary Statistics Styling */
.summary-stats-card {
  border: 1px solid #e0e0e0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.referral-fees-card {
  border: 1px solid #e0e0e0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.referral-fees-total {
  height: 100%;
  padding: 12px;
  text-align: center;
}

.referral-fees-table {
  background: transparent;
}

.referral-fees-table >>> table > tbody > tr > td,
.referral-fees-table >>> table > thead > tr > th {
  white-space: nowrap;
}

.referral-fees-table >>> .v-data-footer__select {
  display: none;
}

/* Attorney Analytics Styling */
.analytics-card {
  border: 1px solid #e0e0e0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Office Statistics Styling */
.office-stat-card {
  height: 100%;
  transition: transform 0.2s, box-shadow 0.2s;
  border: 1px solid #e0e0e0;
}

.office-stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.office-name {
  font-size: 16px;
  font-weight: 600;
  color: #1976d2;
  margin-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 8px;
}

.office-metric {
  margin-bottom: 8px;
}

.office-metric .metric-value {
  font-size: 20px;
  font-weight: 700;
  line-height: 1.1;
  margin-bottom: 2px;
}

.office-metric .metric-label {
  font-size: 12px;
  color: #666;
  font-weight: 500;
}

/* Case Group Statistics Styling */
.case-group-stat-card {
  height: 100%;
  transition: transform 0.2s, box-shadow 0.2s;
  border: 1px solid #e0e0e0;
}

.case-group-stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.case-group-name {
  font-size: 16px;
  font-weight: 600;
  color: #1976d2;
  margin-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 8px;
}

.case-group-metric {
  margin-bottom: 8px;
}

.case-group-metric .metric-value {
  font-size: 20px;
  font-weight: 700;
  line-height: 1.1;
  margin-bottom: 2px;
}

.case-group-metric .metric-label {
  font-size: 12px;
  color: #666;
  font-weight: 500;
}

/* Litigation Breakdown Styling */
.litigation-breakdown {
  margin-top: 8px;
}

.litigation-row {
  display: flex;
  justify-content: space-between;
  gap: 8px;
}

.litigation-metric {
  flex: 1;
  min-width: 0;
}

.litigation-metric .metric-value {
  font-size: 16px;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 2px;
}

.litigation-metric .metric-sublabel {
  font-size: 10px;
  color: #666;
  font-weight: 500;
  line-height: 1.2;
}

.litigation-metric .metric-count {
  font-size: 9px;
  color: #888;
  font-style: italic;
  line-height: 1.1;
}
</style>
